import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Flex, Icon, GridBag, Gutter, Div } from '@gaiads/telia-react-component-library';
import { AdminSpecItem, LocaleResponse, SpecItemTranslation } from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { SpecItemWithKey, SpecItemTranslationWithKey } from '../../../../utils/types';
import { fillArray, removeEntityByIndex, updateEntityByIndex } from '../../../../utils/entities';

interface SpecProps {
  index: number;
  spec: AdminSpecItem;
  handleSpecUpdate: (updatedSpec: SpecItemWithKey) => void;
  handleSpecTranslationUpdate: (spec: SpecItemWithKey, updatedSpecTranslation: SpecItemTranslationWithKey) => void;
  handleSpecRemoval: (specToRemove: SpecItemWithKey) => void;
  locale: LocaleResponse;
  errors: { error?: string; lines?: { index: number; error: string }[] } | undefined;
}

export default function Spec(props: SpecProps) {
  const { index, spec, handleSpecUpdate, handleSpecTranslationUpdate, handleSpecRemoval, locale, errors } = props;
  const { t } = useTranslation('productscreen');

  const [nameReferenceInputOpen, setNameReferenceInputOpen] = useState<boolean>(false);
  const [lineReferenceInputsOpen, setLineReferenceInputsOpen] = useState<boolean[]>(new Array(spec.lines.length).fill(false));

  const setLineReferenceInputOpenState = (lineIndex: number, open: boolean) => {
    const openLineReferenceInputsState = [...lineReferenceInputsOpen];
    openLineReferenceInputsState[lineIndex] = open;
    setLineReferenceInputsOpen(openLineReferenceInputsState);
  };

  const specTranslation = (spec.translations || []).find(t => t.locale_id === locale.id);

  const handleSpecNameChange = (name: AdminSpecItem['name']) => {
    const updatedSpec = { ...spec, name };
    handleSpecUpdate(updatedSpec);
  };
  const handleSpecTranslationNameChange = (name: SpecItemTranslation['name']) => {
    if (specTranslation) {
      const updatedTranslation = { ...specTranslation, name };
      handleSpecTranslationUpdate(spec, updatedTranslation);
    }
  };
  const handleSpecLineChange = (lineIndex: number, value: string) => {
    const updatedSpec = {
      ...spec,
      lines: updateEntityByIndex(spec.lines, lineIndex, value),
    };
    handleSpecUpdate(updatedSpec);
  };
  const handleSpecLineTranslationChange = (lineIndex: number, value: string) => {
    if (specTranslation) {
      // if translations hasn't been created then add initial translations
      const specLineTranslations = fillArray(specTranslation.lines, spec.lines.length);
      const updatedTranslation = {
        ...specTranslation,
        lines: updateEntityByIndex(specLineTranslations, lineIndex, value),
      };
      handleSpecTranslationUpdate(spec, updatedTranslation);
    }
  };
  const handleLineAddition = () => {
    const updatedSpec = {
      ...spec,
      lines: updateEntityByIndex(spec.lines, spec.lines.length, ''),
      translations: (spec.translations || []).map(t => {
        return {
          ...t,
          lines: updateEntityByIndex(t.lines, spec.lines.length, ''),
        };
      }),
    };
    handleSpecUpdate(updatedSpec);
    setLineReferenceInputsOpen([...lineReferenceInputsOpen, false]);
  };
  const handleLineRemoval = (lineIndex: number) => {
    const updatedSpec = {
      ...spec,
      lines: removeEntityByIndex(spec.lines, lineIndex),
      translations: (spec.translations || []).map(translation => {
        return {
          ...translation,
          lines: removeEntityByIndex(translation.lines, lineIndex),
        };
      }),
    };
    handleSpecUpdate(updatedSpec);
    const openLineReferenceInputsState = removeEntityByIndex(lineReferenceInputsOpen, lineIndex);
    setLineReferenceInputsOpen(openLineReferenceInputsState);
  };

  return (
    <Div padding={{ size: 'sm' }} margin={{ vertical: 'sm' }} boxShadow="sm">
      <Flex alignRight>
        <Icon
          name="delete"
          data-testid={`spec-${index}-delete`}
          size="md"
          color="gray500"
          margin={{ bottom: 'sm' }}
          title={t('specs_section.delete_spec')}
          onClick={() => handleSpecRemoval(spec)}
          clickableAppearance
        />
      </Flex>

      <GridBag>
        <GridBag.Item md={4}>
          <EditableItem
            backgroundHoverColor="gray200"
            backgroundColor="gray100"
            divProps={{
              padding: { size: 'sm' },
            }}
          >
            <EditableItem.Input
              value={specTranslation?.name}
              edit={true}
              inputProps={{
                name: `spec-${index}-nameTranslation`,
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleSpecTranslationNameChange(e.target.value),
                placeholder: t('specs_section.spec_name_translation_placeholder', { language: locale.name.split(' ')[0] }),
                label: t('specs_section.spec_name_translation_label'),
                onKeyDown: () => undefined,
              }}
            />
            <Gutter size="sm" />
            <EditableItem.Input
              value={spec.name || ''}
              edit={nameReferenceInputOpen}
              isReference={true}
              focusOnEdit={true}
              inputProps={{
                name: `spec-${index}-name`,
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  handleSpecNameChange(e.target.value);
                  setNameReferenceInputOpen(false);
                },
                placeholder: t('specs_section.spec_name_reference_placeholder'),
                label: t('specs_section.spec_name_reference_label'),
                onKeyDown: () => undefined,
                required: true,
                errorText: errors?.error,
              }}
              editIconProps={{
                'data-testid': `spec-${index}-name-edit`,
                onClick: () => setNameReferenceInputOpen(true),
              }}
            />
          </EditableItem>
        </GridBag.Item>
        <GridBag.Item md={8}>
          {spec.lines.map((line, lineIndex) => (
            <EditableItem
              key={lineIndex}
              backgroundHoverColor="gray200"
              backgroundColor="gray100"
              divProps={{
                padding: { size: 'sm' },
                margin: { bottom: 'sm' },
              }}
            >
              <EditableItem.Input
                value={specTranslation?.lines[lineIndex] || ''}
                edit={true}
                inputProps={{
                  name: `spec-${index}-line-${lineIndex}-translation`,
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleSpecLineTranslationChange(lineIndex, e.target.value),
                  placeholder: t('specs_section.spec_line_translation_placeholder', { language: locale.name.split(' ')[0] }),
                  label: t('specs_section.spec_line_translation_label', { index: lineIndex + 1 }),
                  onKeyDown: () => undefined,
                }}
              />
              <Gutter size="sm" />
              <EditableItem.Input
                value={line || ''}
                edit={lineReferenceInputsOpen[lineIndex]}
                isReference={true}
                focusOnEdit={true}
                showDelete
                inputProps={{
                  name: `spec-${index}-line-${lineIndex}`,
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    handleSpecLineChange(lineIndex, e.target.value);
                    setLineReferenceInputOpenState(lineIndex, false);
                  },
                  placeholder: t('specs_section.spec_line_reference_placeholder'),
                  label: t('specs_section.spec_line_reference_label', { index: lineIndex + 1 }),
                  onKeyDown: () => undefined,
                  required: true,
                  errorText: errors?.lines?.find(line => line.index === lineIndex)?.error,
                }}
                editIconProps={{
                  'data-testid': `spec-${index}-line-${lineIndex}-edit`,
                  onClick: () => setLineReferenceInputOpenState(lineIndex, true),
                }}
                deleteIconProps={{
                  'data-testid': `spec-${index}-line-${lineIndex}-delete`,
                  onClick: () => handleLineRemoval(lineIndex),
                }}
              />
            </EditableItem>
          ))}
          <Div alignTextToCenter>
            <BodyText data-testid={`spec-${index}-add-line`} clickableAppearance={true} color="purple500" onClick={() => handleLineAddition()}>
              {`+ ${t('specs_section.add_line')}`}
            </BodyText>
          </Div>
        </GridBag.Item>
      </GridBag>
    </Div>
  );
}
